import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Customer service review: 1-5 stars + optional comment. One review per
// customer (submitting again updates it). Feeds the public landing rating.
export const ServiceReviewCard = () => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [reply, setReply] = useState(null);

  useEffect(() => {
    api.get('/reviews/mine').then((r) => {
      if (r.data) {
        setRating(r.data.rating || 0);
        setComment(r.data.comment || '');
        setSaved(true);
        setReply(r.data.reply || null);
      }
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!rating) return toast.error('Please select a star rating');
    setBusy(true);
    try {
      await api.post('/reviews', { rating, comment });
      setSaved(true);
      toast.success(saved ? 'Your review has been updated — thank you!' : 'Thank you for rating our service!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save your review'); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="service-review-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Star className="h-4 w-4 text-accent fill-accent" />
          {saved ? 'Your Service Review' : 'Rate Our Service'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-1" data-testid="review-star-picker">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" onClick={() => setRating(i)}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
              className="p-1 rounded transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`${i} star${i > 1 ? 's' : ''}`} data-testid={`review-star-${i}`}>
              <Star className={`h-7 w-7 transition-colors ${(hover || rating) >= i ? 'text-accent fill-accent' : 'text-muted-foreground/40'}`} />
            </button>
          ))}
          {rating > 0 && <span className="ml-2 text-sm font-semibold" data-testid="review-rating-label">{rating}/5</span>}
        </div>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 500))} rows={3}
          placeholder="Tell us about your delivery experience (optional)" data-testid="review-comment-input" />
        {reply && (
          <div className="rounded-lg bg-muted/60 border border-border p-3" data-testid="review-admin-reply">
            <p className="text-[11px] font-semibold text-primary">Response from {reply.by}</p>
            <p className="text-sm mt-1">{reply.text}</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{comment.length}/500</span>
          <Button onClick={submit} disabled={busy || !rating} data-testid="review-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (saved ? 'Update Review' : 'Submit Review')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
